import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import axios from 'axios';
import { glassSelect, fadeInUp } from './glassMorphismStyles';

interface PublicICP {
  id: string;
  title: string;
  data: string;
  default: boolean;
}

interface GlassICPSelectorProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  animationDelay?: number;
}

const GlassICPSelector: React.FC<GlassICPSelectorProps> = ({
  value,
  onChange,
  error,
  animationDelay = 0,
}) => {
  const [icps, setIcps] = useState<PublicICP[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIcp, setSelectedIcp] = useState<PublicICP | null>(null);

  useEffect(() => {
    const fetchPublicICPs = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        const response = await axios.get(`${API_URL}/api/v1/public/icps`);
        setIcps(response.data.icps || []);
      } catch (error) {
        console.error('Error fetching public ICPs:', error);
        // Set fallback ICPs if API fails
        setIcps([
          {
            id: 'general-audience',
            title: 'General Audience',
            data: 'Broad appeal content for diverse demographics and interests',
            default: true,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicICPs();
  }, []);

  useEffect(() => {
    if (value && icps.length > 0) {
      const selected = icps.find(icp => icp.id === value);
      setSelectedIcp(selected || null);
    } else {
      setSelectedIcp(null);
    }
  }, [value, icps]);

  const handleChange = (event: SelectChangeEvent) => {
    const selectedId = event.target.value;
    const selected = icps.find(icp => icp.id === selectedId);
    setSelectedIcp(selected || null);
    onChange(selectedId);
  };

  return (
    <Box 
      sx={{
        animation: `${fadeInUp} 0.6s ease-out forwards`,
        animationDelay: `${animationDelay}ms`,
        opacity: 0,
      }}
    >
      <FormControl 
        fullWidth 
        size="small"
        error={!!error}
        sx={glassSelect}
      >
        <InputLabel id="persona-label">Audience Persona *</InputLabel>
        <Select
          labelId="persona-label"
          id="persona"
          value={value || ''}
          label="Audience Persona *"
          onChange={handleChange}
          disabled={loading}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: 'rgba(0, 51, 78, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(10, 124, 160, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                '& .MuiList-root': {
                  padding: 0,
                },
                '& .MuiMenuItem-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  padding: '12px 16px',
                  borderBottom: '1px solid rgba(10, 124, 160, 0.1)',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(10, 124, 160, 0.2)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(10, 124, 160, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(10, 124, 160, 0.4)',
                    },
                  },
                },
              },
            },
          }}
        >
          {loading ? (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                <span>Loading personas...</span>
              </Box>
            </MenuItem>
          ) : icps.length > 0 ? (
            icps.map((icp) => (
              <MenuItem 
                key={icp.id} 
                value={icp.id}
              >
                {icp.title}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="">
              No personas available
            </MenuItem>
          )}
        </Select>
        {error && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#ff6b6b', 
              mt: 0.5,
              ml: 1.5,
            }}
          >
            {error}
          </Typography>
        )}
      </FormControl>

      {selectedIcp && (
        <Box 
          className="icp-description"
          sx={{
            display: { xs: 'none', sm: 'block' }, // Hide on mobile
            background: 'rgba(0, 51, 78, 0.2)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(10, 124, 160, 0.2)',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '8px',
            animation: `${fadeInUp} 0.4s ease-out`,
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.875rem',
              fontStyle: 'italic',
              lineHeight: 1.6,
            }}
          >
            {selectedIcp.data}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GlassICPSelector;
